import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { FileText } from "lucide-react";

import messages from "../../assets/locale/messages";
import { clx } from "../../utils/Helpers";
import { UIDocumentRow } from "../ui";
import {
  REQUEST_TYPES,
  fillTemplate,
  formatAmount,
  getCompanyName,
} from "./publishingRequestsUtils";

import "./PublishingRequests.scss";

/** Company, event, amount and crew size of the request being accepted or rejected. */
const RequestSummary = ({ request, amountLabel, emphasizeAmount }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { publishingRequests: t, shared } = messages[lang];
  const isCrewSizeChange = request.type === REQUEST_TYPES.crewSizeChange;
  const { crewSize } = request;

  const rows = [
    { label: t.summary.company, value: getCompanyName(request.company, lang) },
    { label: t.summary.event, value: request.event?.name, divided: true },
    {
      label: isCrewSizeChange ? t.summary.differenceToPay : amountLabel,
      value: fillTemplate(t.amountValue, { amount: formatAmount(request.amount) }),
      strong: true,
    },
    isCrewSizeChange
      ? {
          label: t.summary.changeCrewSize,
          value: fillTemplate(t.summary.changeCrewSizeValue, {
            from: request.currentCrewSizeName ?? "",
            to: crewSize?.name ?? "",
          }),
        }
      : {
          label: t.summary.crewSize,
          // Story format: "Medium (26 – 75 applicants)"; the smallest and a custom size read "up to".
          value: crewSize
            ? fillTemplate(
                crewSize.min > 1 ? t.summary.crewSizeRangeValue : t.summary.crewSizeValue,
                {
                  size: crewSize.name,
                  min: formatAmount(crewSize.min),
                  max: formatAmount(crewSize.max),
                },
              )
            : "",
        },
  ];

  return (
    <>
    <dl className="publishing-request-summary">
      {rows.map(({ label, value, divided, strong }) => (
        <div
          key={label}
          className={clx("publishing-request-summary__row", {
            "publishing-request-summary__row--divided": divided,
          })}
        >
          <dt
            className={
              strong && emphasizeAmount
                ? "text-label-md publishing-request-summary__label--strong"
                : "text-body-sm"
            }
          >
            {label}
          </dt>
          <dd className="text-label-md">{value || "-"}</dd>
        </div>
      ))}
    </dl>
      {/* Same file row as the payroll "Proof of Transaction" popup, with a download action. */}
      {request.payment_proof && (
        <UIDocumentRow
          className="publishing-request-summary__evidence"
          title={request.payment_proof.name}
          subtitle={t.summary.transferEvidence}
          href={request.payment_proof.url}
          downloadLabel={shared.download}
          icon={<FileText strokeWidth={1.75} />}
        />
      )}
    </>
  );
};

RequestSummary.propTypes = {
  request: PropTypes.shape({
    type: PropTypes.string.isRequired,
    company: PropTypes.object,
    event: PropTypes.shape({ name: PropTypes.string }),
    amount: PropTypes.number,
    /** Crew size the request is for: its name and capacity. */
    crewSize: PropTypes.shape({
      name: PropTypes.string,
      /** null on a custom plan, which has no lower bound. */
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    /** Crew size the event is on now, for a crew size change. */
    currentCrewSizeName: PropTypes.string,
    payment_proof: PropTypes.shape({ name: PropTypes.string, url: PropTypes.string }),
  }).isRequired,
  /** Label of the amount row of a publishing request (Crew Fee / Amount Due). */
  amountLabel: PropTypes.string.isRequired,
  /** Reject popups show the amount label in the stronger label style. */
  emphasizeAmount: PropTypes.bool,
};

RequestSummary.defaultProps = {
  emphasizeAmount: false,
};

export default RequestSummary;
